// src/pages/ExportScreen.jsx
// Download journal entries in Zoho CSV, Tally XML, or Generic Excel
// Role 2+ access

import { useState, useEffect } from "react";
import { api } from "../services/api";
import { AppDatePicker, AppSelect } from "../components/shared/FormControls";

export default function ExportScreen({ entity }) {
  const [format,   setFormat]   = useState(entity?.accountingSoftware?.type || "excel");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [status,   setStatus]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const formats = [
    { value: "zoho_api",    label: "Zoho Books — CSV import",  icon: "Z", desc: "Import in Zoho: Accountant → Journal Entries → Import" },
    { value: "tally",       label: "Tally — XML import",       icon: "T", desc: "Import in Tally: Gateway → Import → Vouchers" },
    { value: "excel",       label: "Generic Excel",            icon: "X", desc: "Give to your accountant to enter in any software" },
  ];

  async function download() {
    setLoading(true); setError("");
    const filename = `Entries_${entity.shortCode}_${dateFrom || "all"}_to_${dateTo || "now"}.${format === "tally" ? "xml" : format.includes("zoho") ? "csv" : "xlsx"}`;
    try {
      await api.download(`/entities/${entity._id}/export`,
        { format, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, status: status || undefined },
        filename
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Export journal entries</h1>
      <p style={S.subtitle}>Download entries in the format your accounting software accepts.</p>

      {/* Format selector */}
      <div style={S.card}>
        <div style={S.label}>Export format</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {formats.map(f => (
            <label key={f.value} style={{
              display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
              border: `2px solid ${format === f.value ? "#1B3F6E" : "#CFD8DC"}`,
              borderRadius: 10, cursor: "pointer",
              background: format === f.value ? "#EBF5FB" : "#fff",
            }}>
              <input type="radio" value={f.value} checked={format === f.value}
                onChange={() => setFormat(f.value)} style={{ display: "none" }} />
              <div style={{ width: 32, height: 32, background: format === f.value ? "#1B3F6E" : "#ECEFF1",
                borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                color: format === f.value ? "#fff" : "#607D8B", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                {f.icon}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#1B3F6E" }}>{f.label}</div>
                <div style={{ fontSize: 12, color: "#90A4AE", marginTop: 2 }}>{f.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={S.card}>
        <div style={S.label}>Date range (optional — leave blank for all)</div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
          <AppDatePicker value={dateFrom} onChange={setDateFrom} style={{ width: 160 }} />
          <span style={{ color: "#90A4AE" }}>to</span>
          <AppDatePicker value={dateTo} onChange={setDateTo} style={{ width: 160 }} />
        </div>

        <div style={S.label}>Entry status</div>
        <AppSelect
          value={status}
          onChange={setStatus}
          isClearable
          placeholder="All statuses"
          options={[
            { value: "draft", label: "Draft only" },
            { value: "approved", label: "Approved only" },
            { value: "pushed", label: "Already pushed to Zoho" },
          ]}
        />
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      <button onClick={download} disabled={loading} style={{ ...S.btn, background: loading ? "#90A4AE" : "#1B3F6E" }}>
        {loading ? "Preparing download…" : "⬇ Download entries"}
      </button>

      <div style={{ marginTop: 16, padding: "12px 16px", background: "#FFF8E1", borderRadius: 8, fontSize: 12, color: "#7B4F00" }}>
        <strong>Note:</strong> Only approved or draft entries are included. Reversed entries are excluded automatically.
      </div>
    </div>
  );
}

const S = {
  page:     { maxWidth: 600, margin: "0 auto" },
  h1:       { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: "0 0 6px" },
  subtitle: { fontSize: 14, color: "#607D8B", margin: "0 0 24px" },
  card:     { background: "#fff", borderRadius: 12, padding: 20, marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,.07)" },
  label:    { fontSize: 11, fontWeight: 600, color: "#455A64", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10, display: "block" },
  input:    { padding: "9px 12px", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, fontFamily: "inherit" },
  select:   { width: "100%", padding: "9px 12px", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, fontFamily: "inherit" },
  btn:      { width: "100%", padding: 14, color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  errorBox: { background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "12px 16px", color: "#B71C1C", fontSize: 13, marginBottom: 12 },
};


// ─────────────────────────────────────────────────────────────────────────────
// src/pages/TPAAging.jsx
// Patient-wise, insurer-wise TPA outstanding with aging colour bands
// Role 2+
// ─────────────────────────────────────────────────────────────────────────────

export function TPAAging({ entity }) {
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all"); // all | pending | partial | settled
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    if (!entity?._id) return;
    api.get(`/entities/${entity._id}/tpa-debtors`)
      .then(res => setDebtors(res.debtors || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [entity?._id]);

  const today = new Date();

  const displayed = debtors
    .filter(d => filter === "all" || d.status === filter)
    .filter(d => !search || d.patientName?.toLowerCase().includes(search.toLowerCase()) || d.tpaName?.toLowerCase().includes(search.toLowerCase()))
    .map(d => ({
      ...d,
      days: Math.floor((today - new Date(d.dischargeDate || d.createdAt)) / (1000*60*60*24)),
    }));

  function agingColor(days) {
    if (days <= 30) return { color: "#1A7A4A", bg: "#E8F5EE" };
    if (days <= 60) return { color: "#C85A00", bg: "#FFF3E0" };
    if (days <= 90) return { color: "#B71C1C", bg: "#FFEBEE" };
    return { color: "#7B0000", bg: "#FCE4EC" };
  }

  const totalOutstanding = displayed.reduce((s, d) => s + (d.balance || 0), 0);

  return (
    <div style={S2.page}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={S2.h1}>TPA / Insurance outstanding</h1>
          <div style={{ fontSize: 13, color: "#90A4AE" }}>
            Total: <strong style={{ color: "#1B3F6E" }}>₹{totalOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          type="text" placeholder="Search patient or insurer…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: "8px 14px", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, fontFamily: "inherit", width: 220 }}
        />
        {["all", "pending", "partial", "settled", "written_off"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "8px 14px", border: "1.5px solid " + (filter === f ? "#1B3F6E" : "#CFD8DC"),
            background: filter === f ? "#1B3F6E" : "#fff", color: filter === f ? "#fff" : "#607D8B",
            borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: filter === f ? 600 : 400, textTransform: "capitalize",
          }}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#90A4AE" }}>Loading…</div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.07)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Patient", "Insurer / TPA", "Discharge", "Billed (₹)", "TDS (₹)", "Expected (₹)", "Received (₹)", "Balance (₹)", "Age", "Status"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", background: "#1B3F6E", color: "#fff", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: h.includes("(₹)") ? "right" : "left", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: 32, color: "#90A4AE" }}>No records found</td></tr>
              )}
              {displayed.map((d, i) => {
                const ac = agingColor(d.days);
                return (
                  <tr key={d._id} style={{ background: i % 2 === 0 ? "#fafbfc" : "#fff", borderBottom: "1px solid #F0F4F8" }}>
                    <td style={{ padding: "10px 12px", fontSize: 13 }}>{d.patientName}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13 }}>{d.tpaName}</td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "#607D8B" }}>
                      {d.dischargeDate ? new Date(d.dischargeDate).toLocaleDateString("en-IN") : "—"}
                    </td>
                    {[d.billedAmount, d.tdsDeducted, d.netExpected, d.amountReceived, d.balance].map((v, vi) => (
                      <td key={vi} style={{ padding: "10px 12px", textAlign: "right", fontFamily: "monospace", fontSize: 12 }}>
                        {v?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "—"}
                      </td>
                    ))}
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ ...ac, padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                        {d.days}d
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 12, color: "#607D8B", textTransform: "capitalize" }}>
                        {d.status?.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S2 = {
  page: { maxWidth: 1200, margin: "0 auto" },
  h1:   { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: "0 0 4px" },
};


// ─────────────────────────────────────────────────────────────────────────────
// src/pages/AdminDashboard.jsx
// IICIPL Admin — consolidated view across all entities
// Role 3 only
// ─────────────────────────────────────────────────────────────────────────────

export function AdminDashboard({ entities }) {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/dashboard")
      .then(res => setSummary(res.summary || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalRevenue  = summary.reduce((s, e) => s + (e.mtdRevenue  || 0), 0);
  const totalExpenses = summary.reduce((s, e) => s + (e.mtdExpenses || 0), 0);
  const totalPending  = summary.reduce((s, e) => s + (e.pendingEntries || 0), 0);
  const totalFailed   = summary.reduce((s, e) => s + (e.failedPushes   || 0), 0);

  return (
    <div style={S3.page}>
      <h1 style={S3.h1}>Admin overview</h1>
      <p style={S3.subtitle}>All entities — {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>

      {/* Consolidated stats */}
      <div style={S3.statsRow}>
        {[
          { label: "Total MTD revenue",  value: "₹" + lakh2(totalRevenue),  color: "#1B3F6E", bg: "#EBF5FB" },
          { label: "Total MTD expenses", value: "₹" + lakh2(totalExpenses), color: "#C85A00", bg: "#FFF3E0" },
          { label: "Pending entries",    value: totalPending,                color: totalPending > 0 ? "#C85A00" : "#1A7A4A", bg: totalPending > 0 ? "#FFF3E0" : "#E8F5EE" },
          { label: "Failed Zoho pushes", value: totalFailed,                 color: totalFailed > 0 ? "#B71C1C" : "#1A7A4A", bg: totalFailed > 0 ? "#FFEBEE" : "#E8F5EE" },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: "16px 18px", flex: 1 }}>
            <div style={{ fontSize: 11, color: "#607D8B", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Per-entity cards */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#90A4AE" }}>Loading…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {summary.map(e => (
            <div key={e.entityId} style={S3.entityCard}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1B3F6E" }}>{e.entityName}</div>
                  <div style={{ fontSize: 12, color: "#90A4AE", fontFamily: "monospace", marginTop: 2 }}>{e.shortCode}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {e.pendingEntries > 0 && (
                    <span style={{ background: "#FFF3E0", color: "#C85A00", padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                      {e.pendingEntries} pending
                    </span>
                  )}
                  {e.failedPushes > 0 && (
                    <span style={{ background: "#FFEBEE", color: "#B71C1C", padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                      {e.failedPushes} failed
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <MiniStat label="MTD Revenue"  value={"₹" + lakh2(e.mtdRevenue)}  color="#1B3F6E" />
                <MiniStat label="MTD Expenses" value={"₹" + lakh2(e.mtdExpenses)} color="#C85A00" />
                <MiniStat label="Net"          value={"₹" + lakh2(e.mtdRevenue - e.mtdExpenses)} color={(e.mtdRevenue - e.mtdExpenses) >= 0 ? "#1A7A4A" : "#B71C1C"} />
              </div>
              {(e.pendingEntries > 0 || e.failedPushes > 0) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #F0F4F8", display: "flex", gap: 12 }}>
                  {e.pendingEntries > 0 && (
                    <a href={`/entries?entityId=${e.entityId}&status=draft`} style={{ fontSize: 12, color: "#C85A00", textDecoration: "none", fontWeight: 600 }}>
                      Review {e.pendingEntries} pending entries →
                    </a>
                  )}
                  {e.failedPushes > 0 && (
                    <a href={`/entries?entityId=${e.entityId}&status=push_failed`} style={{ fontSize: 12, color: "#B71C1C", textDecoration: "none", fontWeight: 600 }}>
                      Retry {e.failedPushes} failed pushes →
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, color: "#90A4AE", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function lakh2(n) {
  const num = n || 0;
  if (Math.abs(num) >= 100000) return (num / 100000).toFixed(2) + "L";
  if (Math.abs(num) >= 1000)   return (num / 1000).toFixed(1) + "K";
  return num.toFixed(0);
}

const S3 = {
  page:       { maxWidth: 1000, margin: "0 auto" },
  h1:         { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: "0 0 4px" },
  subtitle:   { fontSize: 13, color: "#90A4AE", margin: "0 0 24px" },
  statsRow:   { display: "flex", gap: 14, marginBottom: 20 },
  entityCard: { background: "#fff", borderRadius: 12, padding: "20px 22px", boxShadow: "0 1px 4px rgba(0,0,0,.07)" },
};
